// Barcodes: Prüfziffer der GTIN (GS1, Modulo 10) und die Schreibweisen, unter denen ein Scanner denselben Artikel
// meldet. Reine Funktionen für Server (Prüfung, Auflösung) und Oberfläche (Fehler direkt im Raster).

export const BARCODE_KINDS = ['gtin', 'internal', 'plu', 'supplier'] as const
export type BarcodeKind = (typeof BARCODE_KINDS)[number]

export const BARCODE_KIND_LABELS: Record<BarcodeKind, string> = {
  gtin: 'GTIN / EAN',
  internal: 'Hausnummer',
  plu: 'PLU',
  supplier: 'Lieferanten-Code',
}

/** Längen einer GTIN: GTIN-8, UPC-A (12), EAN-13, GTIN-14. */
export const GTIN_LENGTHS: readonly number[] = [8, 12, 13, 14]

/** Leerraum entfernen – Scanner und Menschen tippen »4 105250 022003«. */
export const normalizeBarcode = (code: string): string => code.normalize('NFKC').replace(/\s+/g, '')

/** Prüfziffer zu den Ziffern OHNE Prüfziffer (von rechts abwechselnd ×3 und ×1). */
export function gtinCheckDigit(body: string): number {
  let sum = 0
  for (let index = 0; index < body.length; index++) {
    const digit = body.charCodeAt(body.length - 1 - index) - 48
    sum += digit * (index % 2 === 0 ? 3 : 1)
  }
  return (10 - (sum % 10)) % 10
}

export function isValidGtin(code: string): boolean {
  if (!/^\d+$/.test(code) || !GTIN_LENGTHS.includes(code.length)) return false
  return gtinCheckDigit(code.slice(0, -1)) === Number(code.at(-1))
}

/**
 * Was an einem Barcode nicht stimmt – als fertiger Satz für das Feld; null = in Ordnung.
 * GTIN: nur Ziffern, 8/12/13/14 Stellen, Prüfziffer. Alle anderen Arten: 1–48 druckbare Zeichen ohne Leerraum.
 */
export function barcodeProblem(code: string, kind: BarcodeKind = 'gtin'): string | null {
  if (code.length === 0) return 'Bitte einen Barcode eingeben.'
  if (kind !== 'gtin') {
    if (code.length > 48) return 'Der Code ist zu lang (höchstens 48 Zeichen).'
    if (!/^[\x21-\x7e]+$/.test(code)) return 'Der Code darf nur Buchstaben, Ziffern und Satzzeichen ohne Leerraum enthalten.'
    return null
  }
  if (!/^\d+$/.test(code)) return 'Eine GTIN besteht nur aus Ziffern. Für eigene Codes wählen Sie die Art „Hausnummer“.'
  if (!GTIN_LENGTHS.includes(code.length)) {
    return `Eine GTIN hat 8, 12, 13 oder 14 Stellen – eingegeben sind ${code.length}.`
  }
  if (!isValidGtin(code)) {
    return `Die Prüfziffer von ${code} stimmt nicht. Bitte prüfen Sie die Ziffern – vermutlich ist eine vertauscht oder falsch gelesen.`
  }
  return null
}

/**
 * Schreibweisen, unter denen derselbe Handelsartikel gescannt wird: UPC-A (12) = EAN-13 mit führender 0,
 * EAN-13 = GTIN-14 mit führender 0. Die gescannte Form steht vorn.
 */
export function barcodeVariants(code: string): string[] {
  const variants = [code]
  if (/^\d+$/.test(code)) {
    if (code.length === 14 && code.startsWith('0')) variants.push(code.slice(1))
    if (code.length === 13) variants.push(`0${code}`)
    if (code.length === 13 && code.startsWith('0')) variants.push(code.slice(1))
    if (code.length === 12) variants.push(`0${code}`, `00${code}`)
    if (code.length === 14 && code.startsWith('00')) variants.push(code.slice(2))
  }
  return [...new Set(variants)]
}
